import React, { useCallback, useContext, useEffect, useState } from "react";
import noop from "lodash/noop";
import { useHistory } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal";
import CopyActivationLinkModal from "../components/CopyActivationLinkModal";
import CopyJourneyModal from "../components/CopyJourneyModal";
import CreateContextualContentModal from "../components/Journey/CreateContextualContentModal";
import CreateContextualTriggerModal from "../components/Journey/CreateContextualTriggerModal";
import EditCompanyModal from "../components/EditCompanyModal";
import EditMaterialModal from "../components/Journey/EditMaterialModal";
import EditStepModal from "../components/Journey/EditStepModal";
import EditMaterialConditionsModal from "../components/Journey/EditMaterialConditionsModal";
import EditScheduledSlotModal from "../components/Journey/EditScheduledSlotModal";
import DeleteJourneyModal from "../components/DeleteJourneyModal";
import EditSpecialLinkModal from "../components/EditSpecialLinkModal";
import EditTipGroupModal from "../components/EditTipGroupModal";
import EditWelfareBookingModal from "../components/EditWelfareBookingModal";
import EditUserMetaModal from "../components/EditUserMetaModal";
import EditUsersListModal from "../components/UsersLists/EditUsersListModal";
import EditListContentModal from "../components/UsersLists/EditListContentModal";
import SemanticsWithEmptyCTALinkModal from "../components/SemanticsWithEmptyCTALinkModal";
import AssociateTipModal from "../components/AssociateTipModal";
import AssociateNewsModal from "../components/News/AssociateNewsModal";
import SemanticTemplatesGalleryModal from "../components/Interaction/SemanticTemplatesGalleryModal";
import CopyWelfareModal from "../components/CopyWelfareModal";
import SpecificUsersModal from "../components/SpecificUsersModal";
import CopyFlowModal from "../components/Journey/CopyFlowModal";
import SpecificUsersBookingModal from "../components/SpecificUsersBookingModal";
import GalleryImagePreviewModal from "../components/Interaction/GalleryImagePreviewModal";
import SheetImportModal from "../components/Copilot/SheetImportModal";
import CopyHiDeepLinkModal from "../components/CopyHiDeepLinkModal";

const modalTypes = {
  AssociateTip: "AssociateTip",
  AssociateNews: "AssociateNews",
  Confirm: "Confirm",
  CopyActivationLink: "CopyActivationLink",
  CopyFlow: "CopyFlowModal",
  CopyHiDeepLink: "CopyHiDeepLink",
  CopyJourney: "CopyJourney",
  CopyWelfare: "CopyWelfare",
  CreateBrowserTrigger: "CreateBrowserTrigger",
  CreateContextualContent: "CreateContextualContent",
  CreateContextualTrigger: "CreateContextualTrigger",
  CreateTrigger: "CreateTrigger",
  DeleteJourney: "DeleteJourney",
  EditCompany: "EditCompany",
  EditMaterial: "EditMaterial",
  EditMaterialConditions: "EditMaterialConditions",
  EditScheduledSlot: "EditScheduledSlot",
  EditSpecialLink: "EditSpecialLink",
  EditStep: "EditStep",
  EditTipGroup: "EditTipGroup",
  EditUserMeta: "EditUserMeta",
  EditUsersList: "EditUsersList",
  EditListContent: "EditListContent",
  EditWelfareBooking: "EditWelfareBooking",
  GalleryImagePreview: "GalleryImagePreview",
  FilteredGalleryImagePreview: "FilteredGalleryImagePreview",
  SemanticTemplatePreview: "SemanticTemplatePreview",
  SemanticTemplatesGalleryModal: "SemanticTemplatesGalleryModal",
  SemanticsWithEmptyCTALink: "SemanticsWithEmptyCTALink",
  SheetImport: "SheetImport",
  SpecificUsers: "SpecificUsers",
  SpecificUsersBooking: "SpecificUsersBooking",
};

const modals = {
  [modalTypes.CopyActivationLink]: CopyActivationLinkModal,
  [modalTypes.CopyJourney]: CopyJourneyModal,
  [modalTypes.CreateContextualContent]: CreateContextualContentModal,
  [modalTypes.CreateContextualTrigger]: CreateContextualTriggerModal,
  [modalTypes.EditMaterial]: EditMaterialModal,
  [modalTypes.EditMaterialConditions]: EditMaterialConditionsModal,
  [modalTypes.EditStep]: EditStepModal,
  [modalTypes.Confirm]: ConfirmModal,
  [modalTypes.EditCompany]: EditCompanyModal,
  [modalTypes.DeleteJourney]: DeleteJourneyModal,
  [modalTypes.EditSpecialLink]: EditSpecialLinkModal,
  [modalTypes.EditTipGroup]: EditTipGroupModal,
  [modalTypes.EditWelfareBooking]: EditWelfareBookingModal,
  [modalTypes.EditUserMeta]: EditUserMetaModal,
  [modalTypes.EditUsersList]: EditUsersListModal,
  [modalTypes.EditListContent]: EditListContentModal,
  [modalTypes.SemanticsWithEmptyCTALink]: SemanticsWithEmptyCTALinkModal,
  [modalTypes.AssociateTip]: AssociateTipModal,
  [modalTypes.AssociateNews]: AssociateNewsModal,
  [modalTypes.EditScheduledSlot]: EditScheduledSlotModal,
  [modalTypes.CopyFlow]: CopyFlowModal,
  [modalTypes.SemanticTemplatePreview]: SemanticTemplatesGalleryModal,
  [modalTypes.CopyWelfare]: CopyWelfareModal,
  [modalTypes.SpecificUsers]: SpecificUsersModal,
  [modalTypes.SpecificUsersBooking]: SpecificUsersBookingModal,
  [modalTypes.GalleryImagePreview]: GalleryImagePreviewModal,
  [modalTypes.SheetImport]: SheetImportModal,
  [modalTypes.CopyHiDeepLink]: CopyHiDeepLinkModal,
};

const ModalsContext = React.createContext({
  closeModal: noop,
  modalTypes: modalTypes,
  openConfirmation: noop,
  openModal: noop,
});

const useModals = () => useContext(ModalsContext);

const ModalsProvider = ({ children }) => {
  const [modal, setModal] = useState(null);
  const [modal2, setModal2] = useState(null);
  const [modalProps, setModalProps] = useState(null);
  const [modal2Props, setModal2Props] = useState(null);
  const history = useHistory();

  useEffect(() => {
    history.listen(() => {
      setModal(null);
      setModal2(null);
      setModalProps(null);
      setModal2Props(null);
    });
  }, [history]);

  const handleOpen = useCallback((modalToOpen, props) => {
    setModal(modalToOpen);
    setModalProps(props);
  }, []);

  const handleOpen2 = useCallback((modalToOpen, props) => {
    setModal2(modalToOpen);
    setModal2Props(props);
  }, []);

  const handleOpenConfirmation = useCallback(
    ({
      title = "Are you sure?",
      text = "",
      confirmCTATitle = "Confirm",
      closeCTATitle = "Cancel",
      ...props
    } = {}) => {
      setModal2(modalTypes.Confirm);
      setModal2Props({
        closeCTATitle,
        confirmCTATitle,
        text,
        title,
        ...props,
      });
    },
    []
  );

  const handleClose = useCallback(
    (...args) => {
      if (typeof modalProps?.onClose === "function") {
        modalProps.onClose(...args);
      }

      setModal(null);
    },
    [modalProps]
  );

  const handleClose2 = useCallback(
    (...args) => {
      if (typeof modal2Props?.onClose === "function") {
        modal2Props.onClose(...args);
      }

      setModal2(null);
    },
    [modal2Props]
  );

  let Modal = modals[modal];
  if (!Modal && modal === modalTypes.FilteredGalleryImagePreview) {
    // eslint-disable-next-line global-require
    Modal =
      require("../components/Interaction/FilteredGalleryImagePreviewModal").default;
  }
  const Modal2 = modals[modal2];

  return (
    <ModalsContext.Provider
      value={{
        closeModal: handleClose,
        modalTypes,
        openConfirmation: handleOpenConfirmation,
        openModal: handleOpen,
        openModal2: handleOpen2,
      }}
    >
      {Modal && <Modal {...modalProps} onClose={handleClose} />}
      {Modal2 && <Modal2 {...modal2Props} onClose={handleClose2} />}
      {children}
    </ModalsContext.Provider>
  );
};

export { useModals, ModalsProvider };
